import _ from "lodash";

import { RiskRatings } from "./riskScoreUtil";

interface SorterInterface {
  [key: string]: number;
}
const SEVERITIES: SorterInterface = {
  critical: 1,
  high: 2,
  medium: 3,
  low: 4,
  info: 5,
  "": 6,
};

// AI Inventory risk levels: Critical > High > Moderate > Low
const RISK_LEVELS: SorterInterface = {
  critical: 1,
  high: 2,
  moderate: 3,
  low: 4,
  "": 5,
};
const RISK_RATING_WEIGHTS: SorterInterface = {
  [RiskRatings.veryPoorRiskScore]: 1,
  [RiskRatings.poorRiskScore]: 2,
  [RiskRatings.fairRiskScore]: 3,
  [RiskRatings.goodRiskScore]: 4,
  "": 5,
};

const STATUS: SorterInterface = {
  OPEN: 1,
  TRIAGE: 2,
  CONFIRMED: 3,
  IN_PROGRESS: 4,
  ACCEPTRISK: 5,
  FALSEPOSITIVE: 6,
  SUPPRESSED: 7,
  CONTROLLED: 8,
  MITIGATED: 9,
  "": 10,
};

interface Data<T> {
  [key: string]: T;
}

export type SorterFunction<T> = (a: T | Data<T>, b: T | Data<T>) => number;

export function stringSorter<T>(
  key: string,
  descend = false,
): SorterFunction<T> {
  return (a, b) => {
    const valueA: string = _.result(a, key, "") + "";
    const valueB: string = _.result(b, key, "") + "";

    if (descend) {
      return valueB.localeCompare(valueA, "en", { numeric: true });
    }

    return valueA.localeCompare(valueB, "en", { numeric: true });
  };
}
export function numberSorter<T>(
  key: string,
  descend = false,
): SorterFunction<T> {
  return (a, b) => {
    const valueA: number = _.result(a, key, 0);
    const valueB: number = _.result(b, key, 0);

    if (descend) {
      return valueB - valueA;
    }
    return valueA - valueB;
  };
}
export function dateSorter<T>(key: string, descend = false): SorterFunction<T> {
  return (a, b) => {
    const valueA: number = _.result(a, key);
    const valueB: number = _.result(b, key);
    let timeA = valueA,
      timeB = valueB;
    if (!_.isNumber(valueA)) {
      timeA = new Date(valueA).getTime();
    }
    if (!_.isNumber(valueB)) {
      timeB = new Date(valueB).getTime();
    }
    if (descend) {
      return timeB - timeA;
    }
    return timeA - timeB;
  };
}

export function severitySorter<T>(
  key: string,
  descending: boolean,
): SorterFunction<T> {
  return (a, b) => {
    const valueA: number =
      SEVERITIES[(_.result(a, key, a as string) ?? "").toLowerCase()];
    const valueB: number =
      SEVERITIES[(_.result(b, key, b as string) ?? "").toLowerCase()];
    if (descending) {
      return valueA - valueB;
    }
    return valueB - valueA;
  };
}

export function riskRatingSorter<T>(
  key: string,
  descending: boolean,
): SorterFunction<T> {
  return (a, b) => {
    const valueA: number =
      RISK_RATING_WEIGHTS[_.result(a, key, a as string) ?? ""];
    const valueB: number =
      RISK_RATING_WEIGHTS[_.result(b, key, b as string) ?? ""];
    if (descending) {
      return valueA - valueB;
    }
    return valueB - valueA;
  };
}

export function statusSorter<T>(
  key: string,
  descending: boolean,
): SorterFunction<T> {
  return (a, b) => {
    const valueA: number =
      STATUS[(_.result(a, key, a as string) ?? "").toUpperCase()] ?? STATUS[""];
    const valueB: number =
      STATUS[(_.result(b, key, b as string) ?? "").toUpperCase()] ?? STATUS[""];

    return descending ? valueA - valueB : valueB - valueA;
  };
}

export function riskLevelSorter<T>(
  key: string,
  descending: boolean,
): SorterFunction<T> {
  return (a, b) => {
    const valueA: number =
      RISK_LEVELS[(_.result(a, key, a as string) ?? "").toLowerCase()] ??
      RISK_LEVELS[""];
    const valueB: number =
      RISK_LEVELS[(_.result(b, key, b as string) ?? "").toLowerCase()] ??
      RISK_LEVELS[""];
    if (descending) {
      return valueA - valueB;
    }
    return valueB - valueA;
  };
}

const Sorters = {
  STRING: stringSorter,
  NUMBER: numberSorter,
  DATE: dateSorter,
  SEVERITY: severitySorter,
  STATUS: statusSorter,
  RISK_RATING: riskRatingSorter,
  // Adding 'RISKRATING' to support dynamic key mapping (e.g., 'riskRating')
  RISKRATING: riskRatingSorter,
  // AI Inventory risk level sorting (Critical > High > Moderate > Low)
  RISK_LEVEL: riskLevelSorter,
};

export default Sorters;
